import { auxContainer, h1, h2 } from "app/common/components/Containers"
import { useToast } from "app/common/components/ToastProvider"
import { RouteMgr } from "app/common/routes"
import { isEmail, isUrl } from "app/common/utilities/guards"
import createFeedback from "app/feedback/mutations/create"
import { useMutation, useRouter } from "blitz"
import { useFormik } from "formik"
import { taskEither } from "fp-ts"
import { pipe } from "fp-ts/function"
import useTranslation from "next-translate/useTranslation"
import { tw } from "twind"

export const ContactUsForm = () => {
  const { t } = useTranslation("contact-us")

  const validate = (values: {
    comments?: string
    company?: string
    email?: string
    name?: string
    url?: string
  }) => {
    const errors: typeof values = {}
    if (!values.email) {
      errors.email = t("email-is-required")
    } else if (!isEmail(values.email)) {
      errors.email = t("invalid-email-address")
    }

    if (values.url && !isUrl(values.url)) {
      errors.url = t("invalid-url")
    }

    if (!values.name) {
      errors.name = t("your-name-is-required")
    }

    if (!values.company) {
      errors.company = t("company-name-is-required")
    }

    return errors
  }

  const { failure, success } = useToast()

  const [createFeedbackMutation] = useMutation(createFeedback)

  const router = useRouter()

  const formik = useFormik({
    initialValues: {
      comments: "",
      company: "",
      email: "",
      name: "",
      url: "",
    },
    onSubmit: (values) =>
      pipe(
        taskEither.tryCatch(
          () => createFeedbackMutation(values),
          (reason) => new Error(String(reason))
        ),
        taskEither.match(
          () => {
            failure(t("failure"))

            return {}
          },
          () => {
            success(t("success"))
            router.push(RouteMgr.Home() || "/")

            return {}
          }
        )
      )(),
    validate,
  })

  return (
    <div className={auxContainer}>
      <h1 className={h1}>{t("contact-us")}</h1>
      <h2 className={h2}>{t("we-would-love")}</h2>
      <p>{t("feel-free")}</p>

      <form className={tw(`flex(& col) gap-16`)} onSubmit={formik.handleSubmit}>
        <ul className={tw(`flex(& col) gap-6`, `mt-8`)}>
          <li className={tw(`flex(& col) gap-1`)}>
            <label className={tw(`font-semibold`)} htmlFor="company">
              {t("company-name")}
            </label>
            <input
              className={tw(`border`, `px-4 py-2`)}
              id="company"
              name="company"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="text"
              value={formik.values.company}
            />
            {formik.touched.company && formik.errors.company && (
              <div className={tw(`text-red-600 font-semibold`)}>{formik.errors.company}</div>
            )}
          </li>

          <li className={tw(`flex(& col) gap-1`)}>
            <label className={tw(`font-semibold`)} htmlFor="url">
              {t("url")}
            </label>
            <input
              className={tw(`border px-4 py-2`)}
              id="url"
              name="url"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="text"
              value={formik.values.url}
            />
            {formik.touched.url && formik.errors.url && (
              <div className={tw(`text-red-600 font-semibold`)}>{formik.errors.url}</div>
            )}
          </li>

          <li className={tw(`flex(& col) gap-1`)}>
            <label className={tw(`font-semibold`)} htmlFor="info">
              {t("comments")}
            </label>
            <textarea
              className={tw(`border px-4 py-2`)}
              id="comments"
              name="comments"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.comments}
            />
            {formik.touched.comments && formik.errors.comments && (
              <div className={tw(`text-red-600 font-semibold`)}>{formik.errors.comments}</div>
            )}
          </li>

          <li className={tw(`flex(& col) gap-1`)}>
            <label className={tw(`font-semibold`)} htmlFor="email">
              {t("your-name")}
            </label>
            <input
              className={tw(`border`, `px-4 py-2`)}
              id="name"
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="text"
              value={formik.values.name}
            />
            {formik.touched.name && formik.errors.name && (
              <div className={tw(`text-red-600 font-semibold`)}>{formik.errors.name}</div>
            )}
          </li>

          <li className={tw(`flex(& col) gap-1`)}>
            <label className={tw(`font-semibold`)} htmlFor="email">
              {t("email-address")}
            </label>
            <input
              className={tw(`border`, `px-4 py-2`)}
              id="email"
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="text"
              value={formik.values.email}
            />
            {formik.touched.email && formik.errors.email && (
              <div className={tw(`text-red-600 font-semibold`)}>{formik.errors.email}</div>
            )}
          </li>
        </ul>

        <button
          className={tw(`bg-red-600`, `text-white font-semibold`, `px-4 py-3`, `self-end`)}
          type="submit"
        >
          Submit
        </button>
      </form>
    </div>
  )
}
